/**
 * #19938 — Giờ làm việc là trần thời gian: cùng luật với `WorkingHoursCeiling` ở backend, dùng để
 * báo lỗi NGAY khi nhập trong form Buổi khám / Lịch trực (server vẫn kiểm lại khi lưu).
 *
 * <ul>
 *   <li>Buổi khám thường (áp cho mọi ngày) phải nằm trong khung RỘNG NHẤT của tuần.
 *   <li>Ca theo thứ / ngày phải nằm trong giờ của đúng thứ đó.
 *   <li>Thứ chưa cấu hình (hoặc giờ không hợp lệ) = không giới hạn.
 * </ul>
 */
import type { DayHours, DayKey, WorkingHours } from "./api/clinic";

const KEYS: DayKey[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const LABELS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7", "Chủ nhật"];

const HHMM = /^\d{2}:\d{2}$/;

function valid(h: DayHours | null | undefined): h is DayHours {
  return !!h && HHMM.test(h.open) && HHMM.test(h.close) && h.open < h.close;
}

/** Giờ của thứ ISO {@code weekday} (1 = Thứ 2 … 7 = CN); null = không giới hạn. */
export function hoursOnWeekday(wh: WorkingHours, weekday: number): DayHours | null {
  const h = wh[KEYS[weekday - 1]];
  return valid(h) ? h : null;
}

/** Giờ mở sớm nhất – đóng muộn nhất trong các thứ đã cấu hình; null = chưa thứ nào cấu hình. */
export function widestHours(wh: WorkingHours): DayHours | null {
  let open: string | null = null;
  let close: string | null = null;
  for (const k of KEYS) {
    const h = wh[k];
    if (!valid(h)) continue;
    if (open === null || h.open < open) open = h.open;
    if (close === null || h.close > close) close = h.close;
  }
  return open && close ? { open, close } : null;
}

/** "HH:mm" so sánh được bằng chuỗi; khoảng [start, end] có nằm trọn trong giờ {@code h} không. */
function fits(h: DayHours, start: string, end: string): boolean {
  return start >= h.open && end <= h.close;
}

/**
 * Lời báo khi một Buổi khám thường vượt giờ làm việc; null khi hợp lệ, chưa đủ giờ, hoặc là Buổi
 * ngoài giờ.
 */
export function sessionOutsideMessage(
  wh: WorkingHours,
  start: string,
  end: string,
  afterHours: boolean,
): string | null {
  if (afterHours || !HHMM.test(start) || !HHMM.test(end)) return null;
  const h = widestHours(wh);
  if (!h || fits(h, start, end)) return null;
  return `Buổi khám ${start}–${end} vượt giờ làm việc của phòng khám (${h.open}–${h.close}). Bật "Nhận lịch ngoài Giờ làm việc" nếu phòng khám nhận lịch vào khoảng giờ này.`;
}

/**
 * Lời báo khi một ca theo giờ vượt giờ làm việc của (các) thứ của nó; null khi hợp lệ hoặc chưa
 * đủ giờ. {@code weekdays} là các thứ ISO của ca (ca theo ngày cụ thể → thứ của ngày đó).
 */
export function shiftOutsideMessage(
  wh: WorkingHours,
  weekdays: number[],
  start: string,
  end: string,
): string | null {
  if (!HHMM.test(start) || !HHMM.test(end)) return null;
  const bad = weekdays
    .map((d) => ({ d, h: hoursOnWeekday(wh, d) }))
    .filter((x): x is { d: number; h: DayHours } => !!x.h && !fits(x.h, start, end));
  if (bad.length === 0) return null;
  const detail = bad.map((x) => `${LABELS[x.d - 1]} ${x.h.open}–${x.h.close}`).join(", ");
  return `Ca ${start}–${end} vượt giờ làm việc của phòng khám (${detail}).`;
}
